// =============================================================================
// services/dmState.ts — DM live state shared across screens [LR2]
//
// Two small, self-contained module-level stores that the DM list screen
// (app/(tabs)/chat.tsx), the conversation screen (app/dm/[id].tsx) and the
// floating nav badge (components/nav/FloatingTabBar.tsx) all read from. Keeping
// this OUTSIDE component state is what lets a message arriving while you're on a
// different screen still bump the unread count + light the nav dot live, and lets
// "mark read" in the thread clear the dot everywhere instantly.
//
// 1) UNREAD STORE — per-conversation unread counts + a derived total. Updated
//    live by the socket dm:message handler (bump on inbound to a non-active
//    thread), cleared on read, and RECONCILED from the server on reconnect via
//    fetchUnreadCounts()/resync (server is always the source of truth for counts;
//    these client deltas just make the UI feel instant between fetches).
//
// 2) SEND QUEUE — an offline outbox. When a DM send fails because we're offline,
//    it's parked here (persisted to AsyncStorage so it survives a cold start) and
//    flushed in order when connectivity returns. Each queued send carries a stable
//    clientMessageId so a flush that races a manual retry can't double-create.
//
// Everything here is crash-safe and additive: a failure never throws into a
// render path; the worst case is a stale badge that the next reconnect reconciles.
// =============================================================================
import AsyncStorage from '@react-native-async-storage/async-storage';
import { api } from './api';
import { onReconnect } from './socket';
import { getSharedKey, encrypt } from './e2e';
import { DM_E2E_ENABLED } from '../lib/comingSoon';

// ── 1) UNREAD STORE ──────────────────────────────────────────────────────────

type UnreadListener = () => void;
const unreadListeners = new Set<UnreadListener>();
// conversationId -> unread count (server-authoritative, locally nudged).
let unreadByConversation: Record<string, number> = {};

function emitUnread() {
  unreadListeners.forEach((l) => {
    try {
      l();
    } catch {
      // a bad subscriber must never break the others
    }
  });
}

/** Subscribe to unread-count changes. Returns an unsubscribe fn. */
export function onDmUnreadChange(cb: UnreadListener): () => void {
  unreadListeners.add(cb);
  return () => {
    unreadListeners.delete(cb);
  };
}

/** Total unread across all DM conversations (drives the nav dot). */
export function getDmUnreadTotal(): number {
  let total = 0;
  for (const k in unreadByConversation) total += unreadByConversation[k] || 0;
  return total;
}

/** Unread count for a single conversation. */
export function getDmUnreadFor(conversationId: string): number {
  return unreadByConversation[conversationId] || 0;
}

/** Snapshot of the whole per-conversation map (for list rendering). */
export function getDmUnreadMap(): Record<string, number> {
  return unreadByConversation;
}

/** Set the unread count for one conversation (e.g. seed from REST list). */
export function setDmUnread(conversationId: string, count: number): void {
  if (!conversationId) return;
  const next = Math.max(0, Math.floor(count) || 0);
  if (unreadByConversation[conversationId] === next) return;
  unreadByConversation = { ...unreadByConversation, [conversationId]: next };
  emitUnread();
}

/** +1 unread for a conversation (an inbound message to a non-active thread). */
export function bumpDmUnread(conversationId: string): void {
  if (!conversationId) return;
  unreadByConversation = {
    ...unreadByConversation,
    [conversationId]: (unreadByConversation[conversationId] || 0) + 1,
  };
  emitUnread();
}

/** Clear unread for a conversation (opened / marked read). */
export function clearDmUnread(conversationId: string): void {
  if (!conversationId) return;
  if (!unreadByConversation[conversationId]) return;
  const next = { ...unreadByConversation };
  delete next[conversationId];
  unreadByConversation = next;
  emitUnread();
}

/**
 * Replace the whole map from a server reconcile (fetchUnreadCounts /
 * resync.dmUnreadByConversation). Server is authoritative — this overwrites any
 * locally-accumulated deltas so a reconnect always converges to the truth.
 */
export function reconcileDmUnread(map: Record<string, number> | null | undefined): void {
  const clean: Record<string, number> = {};
  if (map && typeof map === 'object') {
    for (const k in map) {
      const v = Math.max(0, Math.floor(Number(map[k])) || 0);
      if (v > 0) clean[k] = v;
    }
  }
  unreadByConversation = clean;
  emitUnread();
}

// ── 2) OFFLINE SEND QUEUE ─────────────────────────────────────────────────────

const QUEUE_KEY = 'cryb.dm.sendQueue.v1';

export interface QueuedDmSend {
  clientMessageId: string;
  conversationId: string;
  // The PLAINTEXT the user typed (E2E sealing happens at flush time with the
  // current shared key — we never persist ciphertext, and re-seal on flush).
  content: string;
  createdAt: string; // ISO — preserves intended order
  // The peer's username/id, captured at enqueue time. The GLOBAL outbox flush
  // (startDmOutbox) needs it to fetch the shared key and re-seal E2E without a
  // mounted DMScreen. Optional/graceful: absent → the flush sends plaintext.
  peerUsername?: string;
}

// In-memory mirror of the persisted queue (kept in send order, oldest first).
let queue: QueuedDmSend[] = [];
let hydrated = false;

type QueueListener = () => void;
const queueListeners = new Set<QueueListener>();

function emitQueue() {
  queueListeners.forEach((l) => {
    try {
      l();
    } catch {
      // ignore
    }
  });
}

async function persist() {
  try {
    await AsyncStorage.setItem(QUEUE_KEY, JSON.stringify(queue));
  } catch {
    // ignore — the in-memory copy still flushes this session
  }
}

/** Load the persisted queue once (call on app/DM-screen mount). Idempotent. */
export async function hydrateSendQueue(): Promise<void> {
  if (hydrated) return;
  hydrated = true;
  try {
    const raw = await AsyncStorage.getItem(QUEUE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        queue = parsed.filter(
          (q) => q && typeof q.clientMessageId === 'string' && typeof q.conversationId === 'string'
        );
        emitQueue();
      }
    }
  } catch {
    // ignore — start with an empty queue
  }
}

/** Subscribe to queue changes (e.g. to show a pending-outbox hint). */
export function onSendQueueChange(cb: QueueListener): () => void {
  queueListeners.add(cb);
  return () => {
    queueListeners.delete(cb);
  };
}

/** Enqueue an offline send. Dedupes by clientMessageId. */
export async function enqueueDmSend(item: QueuedDmSend): Promise<void> {
  if (!item?.clientMessageId || !item.conversationId) return;
  if (queue.some((q) => q.clientMessageId === item.clientMessageId)) return;
  queue = [...queue, item];
  emitQueue();
  await persist();
}

/** Remove a queued send once it's confirmed sent (or permanently abandoned). */
export async function dequeueDmSend(clientMessageId: string): Promise<void> {
  const before = queue.length;
  queue = queue.filter((q) => q.clientMessageId !== clientMessageId);
  if (queue.length !== before) {
    emitQueue();
    await persist();
  }
}

/** Queued sends for a specific conversation (send order). */
export function getQueuedForConversation(conversationId: string): QueuedDmSend[] {
  return queue.filter((q) => q.conversationId === conversationId);
}

/** The whole queue (send order). */
export function getSendQueue(): QueuedDmSend[] {
  return queue;
}

// ── 3) GLOBAL OUTBOX FLUSH ─────────────────────────────────────────────────────
//
// The per-thread flush in DMScreen only runs while THAT conversation is mounted,
// so a queued (failed) send to a thread you've since navigated away from would
// never leave the outbox until you reopened that exact conversation. This drains
// the WHOLE persisted queue on reconnect regardless of which screen is mounted.
//
// Safe against double-send: every queued item carries a stable clientMessageId
// that the server insert dedupes on, so racing the per-thread flush (or a manual
// retry) can never double-create. A failed POST is simply left in the queue for
// the next reconnect. A mounted DMScreen showing the same thread will also try —
// both paths dequeue on success and no-op safely.

let flushing = false;

/**
 * Drain the entire persisted send queue in order. Each item is re-sealed against
 * the peer's CURRENT shared key at send time (keys may have rotated while queued),
 * falling back to plaintext when E2E is off or no key is available. Crash-safe:
 * never throws; a failed send stays queued for the next attempt. Re-entrant-safe.
 */
export async function flushSendQueue(): Promise<void> {
  if (flushing) return;
  flushing = true;
  try {
    await hydrateSendQueue();
    // Snapshot in send order; dequeueDmSend mutates `queue` as we go.
    const pending = [...queue];
    for (const q of pending) {
      // Skip if a concurrent flush/retry already cleared it.
      if (!queue.some((x) => x.clientMessageId === q.clientMessageId)) continue;
      let wire = q.content;
      if (DM_E2E_ENABLED && q.peerUsername) {
        try {
          const shared = await getSharedKey(q.peerUsername);
          if (shared) {
            const sealed = encrypt(q.content, shared);
            if (sealed) wire = sealed;
          }
        } catch {
          // keep plaintext — sending is never blocked
        }
      }
      let res;
      try {
        res = await api<{ message: any }>(
          `/direct-messages/conversations/${q.conversationId}/messages`,
          {
            method: 'POST',
            body: JSON.stringify({ content: wire, clientMessageId: q.clientMessageId }),
          },
        );
      } catch {
        res = { success: false } as any;
      }
      if (res?.success) {
        await dequeueDmSend(q.clientMessageId);
      }
      // On failure: leave it queued for the next reconnect (no throw).
    }
  } catch {
    // never throw into a socket/render path
  } finally {
    flushing = false;
  }
}

let outboxStarted = false;

/**
 * Arm the app-global outbox: flush the persisted queue on every reconnect and
 * once now (covers the just-came-online case). Idempotent — safe to call from
 * multiple screens; only the first call wires the listener.
 */
export function startDmOutbox(): void {
  if (outboxStarted) return;
  outboxStarted = true;
  try {
    onReconnect(() => {
      flushSendQueue().catch(() => {});
    });
  } catch {
    // socket not ready — the immediate flush below still runs
  }
  flushSendQueue().catch(() => {});
}
